/**
 * Outil de tracé des pistes, en 3D.
 *
 * - Piste neuve (`ui.build` de type « piste ») : chaque clic pose un point de
 *   passage, du départ (sommet d'une remontée, gare, piste existante) vers
 *   l'arrivée (gare aval, piste, village). Double-clic ou Entrée : valider ;
 *   Retour arrière : retirer le dernier point ; Échap : quitter.
 * - Reprise (`ui.pisteEdit`) : les points de passage sont des poignées à
 *   glisser. Un clic sur le tracé en ajoute une, un clic droit (ou Suppr sur
 *   la poignée survolée) en retire une. L'ancien tracé reste dessiné en gris.
 *
 * L'aperçu est contrôlé par les règles partagées (`checkPiste`) avec le même
 * réseau que le serveur : ruban à la largeur réelle, teinté de la difficulté
 * obtenue, rouge si le tracé est refusé. Seul le tronçon qui bouge est
 * recalculé (cache des tronçons).
 */
import * as THREE from 'three/webgpu';
import {
  MAX_PISTE_POINTS,
  analysePiste,
  checkPiste,
  distPointPolyline,
  pisteCost,
  pisteWidth,
  resample,
  routePistePath,
  type PisteCheck,
  type PisteDesignContext,
  type Vec2,
} from '@snowchain/shared';
import type { ClientStore } from '../state/store';
import type { RtsCamera } from './camera';
import { toast, ui } from '../hud/ui-state';
import { pisteView, stopEdit, submitPiste } from '../hud/piste-state';

/** Hauteur de l'aperçu au-dessus du sol (m). */
const ABOVE = 1.3;
/** Teintes de l'aperçu, plus claires que le balisage : lisibles sur la neige à l'ombre. */
const PREVIEW_TINT: Record<string, number> = { verte: 0x4cc276, bleue: 0x3f9be8, rouge: 0xf06a3c, noire: 0x1c232b };
const RED = 0xd0654c;
const GREEN = 0x7fd39a;
const WHITE = 0xf4f7fa;
const EMBER = 0xf0a050;
const OLD = 0x8796a4;
/** Rayon de saisie d'une poignée (pixels). */
const GRAB_PX = 16;

type Mode = 'trace' | 'edit';

const round1 = (p: Vec2): Vec2 => [Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10];

/** Abscisse (m, le long de la polyligne) du point de `path` le plus proche de p. */
function along(path: readonly Vec2[], p: Vec2): number {
  let best = Infinity;
  let at = 0;
  let acc = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i] as Vec2;
    const b = path[i + 1] as Vec2;
    const dx = b[0] - a[0];
    const dz = b[1] - a[1];
    const l2 = dx * dx + dz * dz;
    const len = Math.sqrt(l2);
    let t = l2 > 0 ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dz) / l2 : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const d = Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dz * t));
    if (d < best) {
      best = d;
      at = acc + len * t;
    }
    acc += len;
  }
  return at;
}

export class PisteTool {
  readonly group = new THREE.Group();
  private readonly ribbon: THREE.Mesh;
  private readonly ribbonMat: THREE.MeshBasicNodeMaterial;
  private readonly axis: THREE.Line;
  private readonly old: THREE.Line;
  private readonly handleGeo: THREE.CylinderGeometry;
  private readonly handleMats = new Map<number, THREE.MeshBasicNodeMaterial>();
  private readonly handles: THREE.Mesh[] = [];
  private readonly cursor: THREE.Mesh;
  private readonly ends: [THREE.Mesh, THREE.Mesh];
  private readonly segCache = new Map<string, Vec2[]>();
  private readonly proj = new THREE.Vector3();
  private hover: Vec2 | null = null;
  private hoverHandle = -1;
  private dragIndex = -1;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private check: PisteCheck | null = null;
  private oldKey = '';
  private shown = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly cam: RtsCamera,
    private readonly store: ClientStore,
    /** Relief de rendu (terrassements compris), pour poser l'aperçu sur le sol affiché. */
    private readonly ground: { height(x: number, z: number): number },
  ) {
    this.group.name = 'outil-pistes';
    this.ribbonMat = new THREE.MeshBasicNodeMaterial({ transparent: true, opacity: 0.42, depthWrite: false, side: THREE.DoubleSide });
    this.ribbonMat.polygonOffset = true;
    this.ribbonMat.polygonOffsetFactor = -2;
    this.ribbon = new THREE.Mesh(new THREE.BufferGeometry(), this.ribbonMat);
    this.ribbon.frustumCulled = false;
    this.ribbon.renderOrder = 6;
    this.axis = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicNodeMaterial({ color: WHITE, transparent: true, opacity: 0.9, depthTest: false }));
    this.axis.frustumCulled = false;
    this.axis.renderOrder = 7;
    this.old = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicNodeMaterial({ color: OLD, transparent: true, opacity: 0.85, depthTest: false }));
    this.old.frustumCulled = false;
    this.old.renderOrder = 6;
    this.handleGeo = new THREE.CylinderGeometry(3.2, 3.2, 1.2, 24);
    const ring = new THREE.RingGeometry(4.2, 6, 40);
    ring.rotateX(-Math.PI / 2);
    this.cursor = new THREE.Mesh(ring, this.mat(WHITE));
    this.cursor.renderOrder = 9;
    this.cursor.frustumCulled = false;
    this.ends = [new THREE.Mesh(ring, this.mat(GREEN)), new THREE.Mesh(ring, this.mat(GREEN))];
    for (const m of this.ends) {
      m.renderOrder = 9;
      m.frustumCulled = false;
    }
    this.group.add(this.ribbon, this.old, this.axis, this.cursor, ...this.ends);
    this.hide();
    this.bind();
    ui.build.subscribe(() => this.schedule());
    ui.pisteEdit.subscribe(() => this.schedule());
    store.subscribe((parts) => {
      if (this.mode() && (parts.has('pistes') || parts.has('lifts') || parts.has('buildings'))) this.schedule();
    });
  }

  /** Matériau toujours visible (poignées, repères), un par couleur. */
  private mat(color: number): THREE.MeshBasicNodeMaterial {
    let m = this.handleMats.get(color);
    if (!m) {
      m = new THREE.MeshBasicNodeMaterial({ color, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false });
      this.handleMats.set(color, m);
    }
    return m;
  }

  private mode(): Mode | null {
    if (ui.pisteEdit.value) return 'edit';
    if (ui.build.value?.kind === 'piste') return 'trace';
    return null;
  }

  private points(): Vec2[] {
    const e = ui.pisteEdit.value;
    if (e) return e.points;
    const b = ui.build.value;
    return b?.kind === 'piste' ? b.points : [];
  }

  private setPoints(pts: Vec2[]): void {
    const e = ui.pisteEdit.value;
    if (e) {
      ui.pisteEdit.value = { ...e, points: pts };
      return;
    }
    const b = ui.build.value;
    if (b?.kind === 'piste') ui.build.value = { ...b, points: pts };
  }

  /* -------------------------------------------------------------- */
  /* Souris et clavier                                                */
  /* -------------------------------------------------------------- */

  private bind(): void {
    const c = this.canvas;
    c.addEventListener('pointermove', (e) => this.onMove(e));
    c.addEventListener('pointerleave', () => {
      if (!this.mode() || this.dragIndex >= 0) return;
      this.hover = null;
      this.hoverHandle = -1;
      this.schedule();
    });
    // Capture : saisir une poignée ne doit pas faire glisser la caméra.
    c.addEventListener('pointerdown', (e) => this.onDown(e), { capture: true });
    window.addEventListener('pointerup', (e) => this.onUp(e));
    c.addEventListener('dblclick', (e) => {
      if (this.mode() !== 'trace') return;
      e.preventDefault();
      void submitPiste();
    });
    window.addEventListener('keydown', (e) => this.onKey(e));
  }

  private onMove(e: PointerEvent): void {
    const mode = this.mode();
    if (!mode) return;
    const p = this.cam.pick(e.clientX, e.clientY);
    this.hover = p ? [p.x, p.z] : null;
    if (this.dragIndex >= 0) {
      if (this.hover) {
        const pts = this.points().slice();
        pts[this.dragIndex] = this.snap(this.hover, this.dragIndex, pts.length);
        this.setPoints(pts);
      }
      return;
    }
    if (mode === 'edit') {
      this.hoverHandle = this.handleAt(e.clientX, e.clientY);
      this.canvas.style.cursor = this.hoverHandle >= 0 ? 'grab' : '';
    }
    this.schedule();
  }

  private onDown(e: PointerEvent): void {
    if (this.mode() !== 'edit') return;
    const i = this.handleAt(e.clientX, e.clientY);
    if (i < 0) return;
    if (e.button === 0) {
      this.dragIndex = i;
      this.cam.enabled = false;
      this.canvas.style.cursor = 'grabbing';
      e.stopImmediatePropagation();
    } else if (e.button === 2) {
      e.stopImmediatePropagation();
      this.removePoint(i);
    }
  }

  private onUp(e: PointerEvent): void {
    const mode = this.mode();
    if (this.dragIndex >= 0) {
      this.dragIndex = -1;
      this.cam.enabled = true;
      this.canvas.style.cursor = '';
      this.schedule();
      return;
    }
    if (!mode || e.button !== 0 || this.cam.dragged || e.target !== this.canvas) return;
    const p = this.cam.pick(e.clientX, e.clientY);
    if (!p) return;
    const q: Vec2 = [p.x, p.z];
    const pts = this.points();
    if (pts.length >= MAX_PISTE_POINTS) {
      toast('warn', 'Tracé complet', `${MAX_PISTE_POINTS} points de passage au plus.`);
      return;
    }
    if (mode === 'trace') {
      const net = this.store.network();
      if (!pts.length && net && !net.anchorAt(q, 'start')) {
        toast('warn', 'Départ impossible ici', 'Partez du sommet d\u2019une remontée, d\u2019une gare ou d\u2019une piste existante.');
        return;
      }
      const s = this.snap(q, pts.length, pts.length + 1);
      const last = pts[pts.length - 1];
      // Second clic d'un double-clic : le point est déjà posé.
      if (last && Math.hypot(last[0] - s[0], last[1] - s[1]) < 3) return;
      this.setPoints([...pts, s]);
      return;
    }
    // Reprise : un clic sur le tracé ajoute une poignée entre ses deux voisines.
    const path = this.check?.path;
    const width = this.check?.width ?? 30;
    if (!path || this.hoverHandle >= 0 || distPointPolyline(q, path) > width / 2 + 4) return;
    const s = along(path, q);
    let at = pts.length - 1;
    for (let i = 1; i < pts.length; i++) {
      if (along(path, pts[i] as Vec2) >= s) {
        at = i;
        break;
      }
    }
    this.setPoints([...pts.slice(0, at), round1(q), ...pts.slice(at)]);
  }

  private onKey(e: KeyboardEvent): void {
    const mode = this.mode();
    const tag = (e.target as HTMLElement | null)?.tagName;
    if (!mode || tag === 'INPUT' || tag === 'TEXTAREA') return;
    if (e.code === 'Enter' || e.code === 'NumpadEnter') {
      e.preventDefault();
      void submitPiste();
    } else if (e.code === 'Backspace' || e.code === 'Delete') {
      e.preventDefault();
      if (mode === 'trace') this.setPoints(this.points().slice(0, -1));
      else if (this.hoverHandle >= 0) this.removePoint(this.hoverHandle);
    } else if (e.code === 'Escape' && mode === 'edit') {
      e.preventDefault();
      stopEdit(false);
    }
  }

  private removePoint(i: number): void {
    const pts = this.points();
    if (pts.length <= 2) {
      toast('warn', 'Point indispensable', 'Une piste garde au moins un départ et une arrivée.');
      return;
    }
    this.hoverHandle = -1;
    this.setPoints(pts.filter((_, k) => k !== i));
  }

  /**
   * Accroche d'un point : le premier sur un départ (sommet, gare, piste) ;
   * au tracé, les suivants sur une arrivée proche (gare aval, piste) ; à la
   * reprise, le dernier seulement. Les autres restent où on les pose.
   */
  private snap(q: Vec2, index: number, count: number): Vec2 {
    const net = this.store.network();
    if (!net) return round1(q);
    const trace = this.mode() === 'trace';
    let role: 'start' | 'end' | null = null;
    if (index === 0) role = 'start';
    else if (trace || index === count - 1) role = 'end';
    if (!role) return round1(q);
    const a = net.anchorAt(q, role, ui.pisteEdit.value?.id);
    return a && a.kind !== 'village' ? round1(a.point) : round1(q);
  }

  /** Poignée sous le curseur (indice), ou -1. */
  private handleAt(x: number, y: number): number {
    const rect = this.canvas.getBoundingClientRect();
    const pts = this.points();
    let best = -1;
    let bestD = GRAB_PX;
    for (let i = 0; i < pts.length; i++) {
      const p = pts[i] as Vec2;
      const v = this.proj.set(p[0], this.ground.height(p[0], p[1]) + ABOVE, p[1]).project(this.cam.camera);
      if (v.z > 1) continue;
      const sx = rect.left + ((v.x + 1) / 2) * rect.width;
      const sy = rect.top + ((1 - v.y) / 2) * rect.height;
      const d = Math.hypot(sx - x, sy - y);
      if (d < bestD) {
        bestD = d;
        best = i;
      }
    }
    return best;
  }

  /* -------------------------------------------------------------- */
  /* Aperçu                                                          */
  /* -------------------------------------------------------------- */

  private schedule(): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.refresh();
    }, 40);
  }

  /** Échelle des repères selon la distance de la caméra (lisibles de près comme de loin). */
  frame(): void {
    if (!this.shown) return;
    const s = Math.max(0.6, Math.min(7, this.cam.distance / 240));
    for (const h of this.handles) h.scale.setScalar(s);
    this.cursor.scale.setScalar(s);
    for (const m of this.ends) m.scale.setScalar(s);
  }

  private hide(): void {
    this.shown = false;
    this.ribbon.visible = false;
    this.axis.visible = false;
    this.old.visible = false;
    this.cursor.visible = false;
    for (const m of this.ends) m.visible = false;
    for (const h of this.handles) h.visible = false;
  }

  private refresh(): void {
    const mode = this.mode();
    const store = this.store;
    const r = store.replica;
    const net = store.network();
    if (!mode || !r || !net) {
      if (this.shown) this.hide();
      this.check = null;
      this.oldKey = '';
      if (!ui.pisteEdit.value && ui.build.value?.kind !== 'piste') pisteView.value = null;
      return;
    }
    this.shown = true;
    const edit = ui.pisteEdit.value;
    const base = this.points();
    const route = edit ? edit.route : ui.build.value?.kind === 'piste' ? ui.build.value.route : 'direct';
    const size = edit ? edit.size : ui.build.value?.kind === 'piste' ? ui.build.value.size : 'normal';
    if (edit && !r.pistes.has(edit.id)) {
      stopEdit(false);
      return;
    }
    this.drawOld(edit ? (r.pistes.get(edit.id)?.path ?? null) : null, edit?.id ?? 0);
    // Tracé neuf : le curseur sert de point suivant (« si vous terminez ici »).
    const hover = mode === 'trace' && this.hover ? this.snap(this.hover, base.length, base.length + 1) : null;
    const pts = hover && (!base.length || Math.hypot(hover[0] - (base.at(-1) as Vec2)[0], hover[1] - (base.at(-1) as Vec2)[1]) > 3) ? [...base, hover] : base;
    this.drawHandles(base);
    this.drawCursor(mode, base, hover);
    if (pts.length < 2) {
      this.check = null;
      this.ribbon.visible = false;
      this.axis.visible = false;
      for (const m of this.ends) m.visible = false;
      const a = hover ? net.anchorAt(hover, 'start') : null;
      pisteView.value = {
        ok: false,
        cost: 0,
        from: a?.label,
        hint: a ? `Départ : ${a.label}` : 'Survolez le sommet d\u2019une remontée, une gare ou une piste',
      };
      this.frame();
      return;
    }
    const ctx: PisteDesignContext = { hf: store.hf, map: store.map, net, lifts: r.lifts.values(), buildings: r.buildings.values(), cash: r.me?.cash, segCache: this.segCache };
    const check = checkPiste(ctx, { points: pts, route, size }, edit?.id);
    this.check = check;
    // Contrôle arrêté avant le tracé (départ ou arrivée sans accroche) : le chemin s'affiche quand même.
    let path = check.path;
    let width = check.width;
    let an = check.analysis;
    if (!path) {
      const avoid = [...r.lifts.values()].map((l) => ({ a: l.bottom, b: l.top, clearance: 22 }));
      path = routePistePath(store.hf, pts, route, avoid, this.segCache);
      an = analysePiste(store.hf, path);
      width = pisteWidth(an.difficulty, size);
      this.check = { ...check, path, width };
    }
    // Tracé neuf qui ne rejoint encore rien : aperçu neutre, à prolonger.
    const pending = mode === 'trace' && !check.ok && !!check.from && check.to === null;
    const color = check.ok ? (PREVIEW_TINT[check.analysis.difficulty] ?? WHITE) : pending ? WHITE : RED;
    if (path.length >= 2) {
      this.drawRibbon(path, width ?? 26, color);
    } else {
      this.ribbon.visible = false;
      this.axis.visible = false;
    }
    this.drawEnds(this.check);
    const end = hover ? net.anchorAt(hover, 'end') : null;
    pisteView.value = {
      ok: check.ok,
      pending,
      reason: check.ok ? undefined : pending ? 'Prolongez jusqu\u2019à une gare aval, une piste ou le village' : check.reason,
      cost: check.ok || !pending ? check.cost : pisteCost(an?.length ?? 0, size),
      hours: check.ok ? check.hours : undefined,
      difficulty: an?.difficulty,
      length: an?.length,
      drop: an?.drop,
      width,
      from: check.from?.label,
      to: check.to?.label,
      hint: mode === 'trace' ? (end ? `Arrivée possible : ${end.label}` : 'Point de passage') : undefined,
    };
    this.frame();
  }

  private drawRibbon(path: Vec2[], width: number, color: number): void {
    const pts = resample(path, 5);
    const n = pts.length;
    const pos = new Float32Array(n * 6);
    const axis = new Float32Array(n * 3);
    const idx: number[] = [];
    const g = this.ground;
    const half = width / 2;
    for (let i = 0; i < n; i++) {
      const a = pts[Math.max(0, i - 1)] as Vec2;
      const b = pts[Math.min(n - 1, i + 1)] as Vec2;
      let dx = b[0] - a[0];
      let dz = b[1] - a[1];
      const l = Math.hypot(dx, dz) || 1;
      dx /= l;
      dz /= l;
      const [x, z] = pts[i] as Vec2;
      const lx = x + dz * half;
      const lz = z - dx * half;
      const rx = x - dz * half;
      const rz = z + dx * half;
      pos.set([lx, g.height(lx, lz) + ABOVE, lz, rx, g.height(rx, rz) + ABOVE, rz], i * 6);
      axis.set([x, g.height(x, z) + ABOVE + 0.4, z], i * 3);
      if (i < n - 1) {
        const k = i * 2;
        idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setIndex(idx);
    this.ribbon.geometry.dispose();
    this.ribbon.geometry = geo;
    this.ribbonMat.color.setHex(color);
    this.ribbon.visible = true;
    const ag = new THREE.BufferGeometry();
    ag.setAttribute('position', new THREE.BufferAttribute(axis, 3));
    this.axis.geometry.dispose();
    this.axis.geometry = ag;
    this.axis.visible = true;
  }

  /** Ancien tracé (reprise), dessiné une fois par piste. */
  private drawOld(path: Vec2[] | null, id: number): void {
    if (!path) {
      this.old.visible = false;
      this.oldKey = '';
      return;
    }
    const key = `${id}:${path.length}:${path[0]?.join(',')}`;
    if (key === this.oldKey) return;
    this.oldKey = key;
    const g = this.ground;
    const pts = resample(path, 8).map((p) => new THREE.Vector3(p[0], g.height(p[0], p[1]) + ABOVE, p[1]));
    this.old.geometry.dispose();
    this.old.geometry = new THREE.BufferGeometry().setFromPoints(pts);
    this.old.visible = true;
  }

  private drawHandles(pts: Vec2[]): void {
    const edit = this.mode() === 'edit';
    while (this.handles.length < pts.length) {
      const h = new THREE.Mesh(this.handleGeo, this.mat(WHITE));
      h.renderOrder = 10;
      h.frustumCulled = false;
      this.handles.push(h);
      this.group.add(h);
    }
    for (let i = 0; i < this.handles.length; i++) {
      const h = this.handles[i] as THREE.Mesh;
      const p = pts[i];
      if (!p) {
        h.visible = false;
        continue;
      }
      h.visible = true;
      h.position.set(p[0], this.ground.height(p[0], p[1]) + ABOVE + 0.6, p[1]);
      const color = i === this.dragIndex || (edit && i === this.hoverHandle) ? EMBER : i === 0 ? GREEN : WHITE;
      h.material = this.mat(color);
    }
  }

  /** Repère sous la souris (tracé neuf) : vert s'il accroche un départ ou une arrivée. */
  private drawCursor(mode: Mode, base: Vec2[], hover: Vec2 | null): void {
    const net = this.store.network();
    if (mode !== 'trace' || !hover || !net) {
      this.cursor.visible = false;
      return;
    }
    const anchor = net.anchorAt(hover, base.length ? 'end' : 'start');
    this.cursor.material = this.mat(anchor ? GREEN : base.length ? WHITE : RED);
    this.cursor.position.set(hover[0], this.ground.height(hover[0], hover[1]) + ABOVE + 0.3, hover[1]);
    this.cursor.visible = true;
  }

  /** Anneaux au départ et à l'arrivée retenus (rouges s'ils n'accrochent rien). */
  private drawEnds(check: PisteCheck): void {
    const pts = this.points();
    const spots: [Vec2 | null, boolean][] = [
      [check.from ? check.from.point : (pts[0] ?? null), !!check.from],
      [check.to ? check.to.point : check.path ? (check.path.at(-1) ?? null) : null, !!check.to],
    ];
    spots.forEach(([p, ok], i) => {
      const m = this.ends[i] as THREE.Mesh;
      if (!p) {
        m.visible = false;
        return;
      }
      m.material = this.mat(ok ? GREEN : RED);
      m.position.set(p[0], this.ground.height(p[0], p[1]) + ABOVE + 0.2, p[1]);
      m.visible = true;
    });
  }
}
